import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import swal from "sweetalert2";
import { handleError } from "@/util/errorHandler";
import users from "../../api/users";
import TableContainerWithLocation from "../Components/TableContainerWithLocation";

const STATUSES = [
  {
    value: "ALL",
    label: "All",
  },
  {
    value: "OPEN",
    label: "Open",
  },
  {
    value: "VERIFIED",
    label: "Verified",
  },
  {
    value: "AUTHORIZED",
    label: "Authorized",
  },
  {
    value: "REJECTED",
    label: "Rejected",
  },
  {
    value: "CANCELED",
    label: "Canceled",
  },
  {
    value: "CLOSED",
    label: "Closed",
  },
];

const headerStyle = {
  backgroundColor: "#D15050",
  color: "white",
};

let tableRef = null;

const askRejectRequest = async id => {
  const html = `
    <span>
      Do you want to reject this request?
    </span>
    <br/>
    <span>Please, enter the reason in the input below.</span>
  `;

  await swal({
    title: `Reject request?`,
    html,
    type: "warning",
    showCancelButton: true,
    confirmButtonText: "Confirm",
    cancelButtonText: "Cancel",
    showLoaderOnConfirm: true,
    input: "text",
    preConfirm: async reason => {
      await users.rejectChangePhoneRequest({ requestId: id, reason });
      tableRef && tableRef.loadAndStoreData();
    },
  }).catch(handleError);
};

const askVerify = async id => {
  const html = `
    <span>
      Do you want to verify this request?
    </span>
    <br/>
    <span>Please, enter SMS code in the input below.</span>
  `;

  await swal({
    title: `Verify request?`,
    html,
    type: "warning",
    showCancelButton: true,
    confirmButtonText: "Confirm",
    cancelButtonText: "Cancel",
    showLoaderOnConfirm: true,
    input: "text",
    preConfirm: async code => {
      await users.verifyChangePhoneRequest({ requestId: id, code });
      tableRef && tableRef.loadAndStoreData();
    },
  }).catch(handleError);
};

const askApproveRequest = async id => {
  const html = `
    <span>
      Do you want to approve this request?
    </span>
  `;

  await swal({
    title: `Approve request?`,
    html,
    type: "warning",
    showCancelButton: true,
    confirmButtonText: "Confirm",
    cancelButtonText: "Cancel",
    showLoaderOnConfirm: true,
    preConfirm: async () => {
      await users.authorizeChangePhoneRequest(id);
      tableRef && tableRef.loadAndStoreData();
    },
  }).catch(handleError);
};

const askSendSms = async id => {
  const html = `
    <span>
      Do you want to send SMS again?
    </span>
  `;

  await swal({
    title: `Send again?`,
    html,
    type: "warning",
    showCancelButton: true,
    confirmButtonText: "Confirm",
    cancelButtonText: "Cancel",
    showLoaderOnConfirm: true,
    preConfirm: async reason => {
      await users.resendSms(id);
    },
  }).catch(handleError);
};

const askCancelRequest = async id => {
  const html = `
    <span>
      Do you want to cancel request?
    </span>
  `;

  await swal({
    title: `Cancel request?`,
    html,
    type: "warning",
    showCancelButton: true,
    confirmButtonText: "Confirm",
    cancelButtonText: "Cancel",
    showLoaderOnConfirm: true,
    preConfirm: async () => {
      await users.cancelChangePhoneRequest(id);
      tableRef && tableRef.loadAndStoreData();
    },
  }).catch(handleError);
};

const actionsFormatter = (cell, row, type) => {
  const { id } = row;

  return (
    <>
      {type === "admin" && row.status === "OPEN" && (
        <>
          <Button color="success" type="button" onClick={() => askVerify(id)}>
            Verify
          </Button>
          <Button color="warning" type="button" onClick={() => askSendSms(id)}>
            Resend SMS
          </Button>
        </>
      )}
      {type === "admin" &&
        (row.status === "OPEN" || row.status === "VERIFIED") && (
          <Button
            color="danger"
            type="button"
            onClick={() => askCancelRequest(id)}>
            Cancel
          </Button>
        )}
      {type === "authorizer" && row.status === "VERIFIED" && (
        <>
          <Button
            color="success"
            type="button"
            onClick={() => askApproveRequest(id)}>
            Approve
          </Button>
          <Button
            color="danger"
            type="button"
            onClick={() => askRejectRequest(id)}>
            Reject
          </Button>
        </>
      )}
    </>
  );
};

const ChangePhoneRequests = ({ type }) => {
  const defaultStatus = type === "admin" ? "OPEN" : "VERIFIED";
  const [status, setStatus] = useState(defaultStatus);

  const loadPagination = async (page = 0, size = 10, search, sort) => {
    const newStatus = status === "ALL" ? undefined : status;
    let result = await users.getChangePhoneRequests({
      page,
      size,
      search,
      sort,
      status: newStatus,
    });
    return result;
  };

  const filterRequests = async event => {
    await setStatus(event.target.value);
    tableRef.loadAndStoreData();
  };

  return (
    <div className="animated fadeIn">
      <Row>
        <Col>
          <Card>
            <CardHeader>Change phone requests</CardHeader>
            <CardBody>
              <Form
                inline
                style={{
                  marginBottom: ".5rem",
                  justifyContent: "flex-end",
                }}>
                <FormGroup
                  className="mb-2 mr-sm-2 mb-sm-0"
                  style={{ paddingRight: "2rem", alignItems: "baseline" }}>
                  <Label htmlFor="type" style={{ marginRight: "1rem" }}>
                    <h6>Status:</h6>
                  </Label>
                  <Input
                    type="select"
                    name="status"
                    id="status"
                    value={status}
                    style={{
                      maxWidth: "300px",
                    }}
                    onChange={filterRequests}>
                    {STATUSES.map(type => {
                      return (
                        <option value={type.value} key={type.value}>
                          {type.label}
                        </option>
                      );
                    })}
                  </Input>
                </FormGroup>
              </Form>

              <TableContainerWithLocation
                loadData={loadPagination}
                wrappedComponentRef={ref => (tableRef = ref)}>
                <BootstrapTable
                  headerStyle={headerStyle}
                  version="4"
                  search
                  pagination
                  striped
                  hover
                  condensed
                  remote>
                  <TableHeaderColumn dataSort width="200" dataField="accountId">
                    Account
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    dataSort
                    width="120"
                    dataField="replacementMobilePrimaryContact">
                    Phone Number
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    dataSort
                    width="120"
                    dataField="creatorLogin">
                    Creator
                  </TableHeaderColumn>
                  <TableHeaderColumn dataSort width="120" dataField="status">
                    Status
                  </TableHeaderColumn>
                  <TableHeaderColumn width="120" dataField="rejectReason">
                    Reject reason
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    width="270"
                    dataField="id"
                    isKey
                    dataFormat={(cell, row) =>
                      actionsFormatter(cell, row, type)
                    }
                    dataAlign="center">
                    Actions
                  </TableHeaderColumn>
                </BootstrapTable>
              </TableContainerWithLocation>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ChangePhoneRequests;
